/**
 * "Buy it now" — adds the selected variant to the cart, then goes straight to checkout.
 *
 * Deliberately not Shopify's `payment_button`. That tag renders whichever express
 * wallets the shop has enabled (Shop Pay, PayPal, Google Pay) and only falls back to
 * a plain "Buy it now" when none apply, so it can't be used when the storefront wants
 * the direct-to-checkout button without the branded wallet buttons.
 *
 * The button submits the surrounding product form's own FormData, so the selected
 * variant, quantity, line-item properties and selling plan all come along without
 * this component needing to know how any of them work.
 */
class BuyNowButton extends HTMLElement {
  #button = null;
  #abortController = new AbortController();

  connectedCallback() {
    this.#button = this.querySelector('button');
    if (!this.#button) return;

    this.#button.addEventListener('click', this.#onClick, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  get #form() {
    return this.closest('product-form-component')?.querySelector('form') ?? this.closest('form');
  }

  #onClick = async (event) => {
    const form = this.#form;
    if (!form) return;

    // The form's own submit handler is AJAX-only and never navigates, so this click
    // must not reach it.
    event.preventDefault();
    event.stopPropagation();

    if (this.#button.disabled) return;

    // Let the browser surface any native validation (required line-item properties)
    // before anything is sent.
    if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

    const formData = new FormData(form);

    // A quantity selector outside the form element still belongs to this purchase.
    const quantityInput = this.closest('product-form-component')?.querySelector('[name="quantity"]');
    if (quantityInput instanceof HTMLInputElement && quantityInput.value) {
      formData.set('quantity', quantityInput.value);
    }

    this.#setBusy(true);

    try {
      const addUrl = window.Theme?.routes?.cart_add_url ?? '/cart/add.js';
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });

      const result = await response.json();

      // Shopify returns a `status` field only when the add failed.
      if (!response.ok || result.status) {
        this.#setBusy(false);
        this.dispatchEvent(
          new CustomEvent('buy-now:error', {
            bubbles: true,
            detail: { message: result.description || result.message },
          })
        );
        return;
      }

      window.location.assign(window.Theme?.routes?.checkout_url ?? '/checkout');
    } catch (error) {
      this.#setBusy(false);
      console.warn('[buy-now] Could not start checkout:', error);
    }
  };

  #setBusy(busy) {
    if (!this.#button) return;
    this.#button.disabled = busy;
    this.#button.setAttribute('aria-busy', busy ? 'true' : 'false');
  }
}

if (!customElements.get('buy-now-button')) {
  customElements.define('buy-now-button', BuyNowButton);
}
